import { Plus, X, Trash2, RotateCcw } from "lucide-react";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { supabase } from "@/lib/supabase";
import type { LiveSession } from "@/lib/types";

type Props = {
  sessions: LiveSession[];
  activeSessionId: string | null;
  onSelect: (id: string) => void;
  onNew: () => void;
  onSessionEnded?: () => void;
  onReactivate?: (id: string) => void;
};

const providerShort: Record<string, string> = {
  "claude-code": "Claude",
  codex: "Codex",
  gemini: "Gemini",
};

function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  return `${days}d ago`;
}

export function SessionSidebar({
  sessions,
  activeSessionId,
  onSelect,
  onNew,
  onSessionEnded,
  onReactivate,
}: Props) {
  const activeSessions = sessions.filter(
    (s) => s.status === "active" || s.status === "starting"
  );
  const endedSessions = sessions.filter(
    (s) => s.status === "ended" || s.status === "error"
  );

  return (
    <div className="w-64 h-full flex flex-col bg-zinc-950 border-r border-zinc-800 shrink-0">
      {/* New Session button */}
      <div className="p-3 border-b border-zinc-800">
        <Button onClick={onNew} className="w-full gap-2" size="sm">
          <Plus className="h-4 w-4" />
          New Session
        </Button>
      </div>

      {/* Session list */}
      <div className="flex-1 overflow-y-auto py-2">
        {/* Active sessions */}
        {activeSessions.length > 0 && (
          <div className="mb-3">
            <div className="px-3 py-1 text-[10px] font-semibold text-zinc-500 uppercase tracking-wider">
              Active
            </div>
            {activeSessions.map((session) => (
              <SessionItem
                key={session.id}
                session={session}
                isActive={session.id === activeSessionId}
                onSelect={onSelect}
                onRemove={onSessionEnded}
              />
            ))}
          </div>
        )}

        {/* Ended sessions */}
        {endedSessions.length > 0 && (
          <div>
            <div className="px-3 py-1 text-[10px] font-semibold text-zinc-500 uppercase tracking-wider">
              Recent
            </div>
            {endedSessions.map((session) => (
              <SessionItem
                key={session.id}
                session={session}
                isActive={session.id === activeSessionId}
                onSelect={onSelect}
                onRemove={onSessionEnded}
                onReactivate={onReactivate}
              />
            ))}
          </div>
        )}

        {sessions.length === 0 && (
          <div className="px-3 py-8 text-center">
            <p className="text-xs text-zinc-500">No sessions yet</p>
          </div>
        )}
      </div>
    </div>
  );
}

function SessionItem({
  session,
  isActive,
  onSelect,
  onRemove,
  onReactivate,
}: {
  session: LiveSession;
  isActive: boolean;
  onSelect: (id: string) => void;
  onRemove?: () => void;
  onReactivate?: (id: string) => void;
}) {
  const isLive =
    session.status === "active" || session.status === "starting";
  const title = session.title || session.repo || "Untitled";

  const handleRemove = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (isLive) {
      // End the session
      await supabase
        .from("live_sessions")
        .update({ status: "ended", ended_at: new Date().toISOString() })
        .eq("id", session.id);
    } else {
      // Delete ended session
      await supabase
        .from("live_sessions")
        .delete()
        .eq("id", session.id);
    }
    onRemove?.();
  };

  return (
    <div
      className={cn(
        "group w-full flex items-center hover:bg-zinc-900 transition-colors cursor-pointer",
        isActive && "bg-zinc-900 border-l-2 border-primary"
      )}
    >
      <button
        onClick={() => onSelect(session.id)}
        className="flex-1 text-left px-3 py-2 min-w-0"
      >
        <div className="flex items-center gap-2">
          <div
            className={cn(
              "h-1.5 w-1.5 rounded-full shrink-0",
              isLive ? "bg-green-500" : "bg-zinc-600"
            )}
          />
          <span className="text-xs font-medium text-zinc-200 truncate">
            {title}
          </span>
        </div>
        <div className="flex items-center gap-2 mt-0.5 pl-3.5">
          <span className="text-[10px] text-zinc-500">
            {providerShort[session.provider] ?? session.provider}
          </span>
          <span className="text-[10px] text-zinc-600">
            {timeAgo(session.started_at)}
          </span>
        </div>
      </button>
      {!isLive && onReactivate && (
        <button
          onClick={(e) => { e.stopPropagation(); onReactivate(session.id); }}
          className="shrink-0 p-1.5 rounded text-zinc-600 hover:text-green-400 opacity-0 group-hover:opacity-100 sm:opacity-100 transition-opacity"
          title="Reactivate session"
        >
          <RotateCcw className="h-3.5 w-3.5" />
        </button>
      )}
      <button
        onClick={handleRemove}
        className="shrink-0 p-1.5 mr-1 rounded text-zinc-600 hover:text-red-400 opacity-0 group-hover:opacity-100 sm:opacity-100 transition-opacity"
        title={isLive ? "End session" : "Delete session"}
      >
        {isLive ? <X className="h-3.5 w-3.5" /> : <Trash2 className="h-3.5 w-3.5" />}
      </button>
    </div>
  );
}
